"use client";

import { useEffect } from "react";
import CustomModal from "@/components/shared/CustomModal";
import { useAuthModal } from "@/hooks/auth/useAuthModal";
import { useUser } from "@/hooks/user/useUser";
import { getAuthModalConfig } from "./auth-modal.config";
import LoginStep from "./login/LoginStep";
import OtpStep from "./otp/OtpStep";
import RegisterStep from "./register/RegisterStep";
import CreatePasswordStep from "./create-password/CreatePasswordStep";
import ForgotPasswordStep from "./forgot-password/ForgotPasswordStep";
import ResetPasswordStep from "./reset-password/ResetPasswordStep";

const AuthModal = () => {
  const { data: user } = useUser();
  const {
    isOpen,
    step,
    phone,
    email,
    authMethod,
    openLogin,
    openRegister,
    setStep,
    setPhone,
    completeAuthSuccess,
    closeAuth,
  } = useAuthModal();

  useEffect(() => {
    const isForgotPasswordFlow =
      sessionStorage.getItem("auth_flow") === "forgot";

    if (user && isOpen && !isForgotPasswordFlow) {
      void closeAuth();
    }
  }, [closeAuth, isOpen, user, step]);

  const handleOpenChange = (nextOpen: boolean) => {
    void (nextOpen ? openLogin() : closeAuth());
  };

  const handleOpenRegister = () => {
    void openRegister();
  };

  const handleBackToLogin = () => {
    void setStep("login");
  };

  const handleOtpSent = (phoneNumber: string) => {
    void setPhone(phoneNumber);
    void setStep("otp");
  };

  // Navigate back to the correct step depending on which flow triggered OTP:
  // "forgot" flow → go back to forgot-password, otherwise → register
  const handleBackFromOtp = () => {
    const previousStep =
      sessionStorage.getItem("auth_flow") === "forgot"
        ? "forgot-password"
        : "register";
    void setStep(previousStep);
  };

  if (!isOpen || !step) return null;

  const { title, description, headerStart } = getAuthModalConfig({
    step,
    otpPhone: phone || "",
    otpEmail: email || "",
    authMethod: authMethod ?? null,
    onBackToLogin: handleBackToLogin,
    onBackFromOtp: handleBackFromOtp,
  });

  let formContent: React.ReactNode;

  switch (step) {
    case "register":
      formContent = (
        <RegisterStep
          onLoginClick={handleBackToLogin}
          onOtpSent={handleOtpSent}
        />
      );
      break;

    case "otp":
      formContent = <OtpStep />;
      break;

    case "create-password":
      formContent = <CreatePasswordStep />;
      break;

    case "forgot-password":
      formContent = <ForgotPasswordStep />;
      break;

    case "reset-password":
      formContent = <ResetPasswordStep />;
      break;

    case "login":
    default:
      formContent = (
        <LoginStep
          onSuccess={() => void completeAuthSuccess()}
          onRegisterClick={handleOpenRegister}
        />
      );
      break;
  }

  const sharedProps = {
    open: isOpen,
    onOpenChange: handleOpenChange,
    title,
    description,
    headerStart,
  };

  return (
    <CustomModal hideDivider {...sharedProps}>
      {formContent}
    </CustomModal>
  );
};

export default AuthModal;
